<template>
    <div
        :class="{ popover: !!position, [`popover-${position}`]: true, active: picking }"
        class="base-date-input hover-disabled"
    >
        <input-group
            glyph="calendar"
            glyph-position="right"
            @click="click_icon"
        >
            <template v-slot:left_addon><slot name="left_addon"></slot></template>
            <base-input
                ref="field"
                :id="identifier"
                type="text"
                :name="name"
                :placeholder="placeholder"
                :value="format_date_time(value, dateFormat)"
                :aria-describedby="!!has_slot('hint')?`${identifier}-description`:false"
                :disabled="disabled"
                :required="required"
                @focus="focused"
                @touchstart="touchstart"
                @keyup="$emit('keyup', $event)"
                @keydown="keydown"
                @blur="blurred"
            />
        </input-group>
        <div
            v-if="!!position"
            ref="calendar"
            class="popover-container"
        >
            <base-calendar
                :min="min"
                :max="max"
                :selection="value"
                :size="size"
                @click="pick"
            />
        </div>
    </div>
</template>

<script>
import debounce from "lodash/debounce"

import is_nibnut_component from "../../mixins/IsNibnutComponent"
import handles_dates from "../../mixins/HandlesDates"
import is_alpha_numerical_input from "../../mixins/IsAlphaNumericalInput"

import InputGroup from "./InputGroup"
import BaseInput from "./BaseInput"
import BaseCalendar from "./BaseCalendar"

export default {
    name: "BaseDateInput",
    mixins: [is_nibnut_component, handles_dates, is_alpha_numerical_input],
    components: {
        InputGroup,
        BaseInput,
        BaseCalendar
    },
    watch: {
        editable: "maybe_focus_field"
    },
    methods: {
        focus_field () {
            this.$refs.field.$el.focus()
            // this.$refs.field.$el.select()
        },
        maybe_focus_field () {
            if(this.editable) setTimeout(this.focus_field, 150)
        },
        focused () {
            this.$refs.field.$el.select()
            this.picking = true
        },
        keydown: debounce(function (event) {
            if(this.alphanumeric && this.iOS) this.touching = false
        }, 300),
        set_date (date) {
            date = date ? this.standardized_date(date) : null
            let value = date
            if(date && this.ranged) {
                value = this.value ? { ...this.value } : { from: null, to: null }
                let field = this.range_field
                if(date.isBefore(value.from)) field = "from"
                else if(date.isAfter(value.to)) field = "to"
                else this.range_field = (field === "from") ? "to" : "from"
                value[field] = date
                if((field === "from") && !value.to) value.to = date
            }
            this.date_changed(value)
        },
        date_changed (date_value) {
            this.$emit("input", date_value, this.name)
        },
        blurred (event) {
            if(this.alphanumeric && this.iOS) this.touching = false
            const is_calendar_clicked = !!event.relatedTarget && !!this.$refs.calendar && this.$refs.calendar.contains(event.relatedTarget)
            if(is_calendar_clicked) return

            if(!event.target.value) this.set_date(null)
            else {
                let parsed_value = null
                if(this.ranged) {
                    parsed_value = this.value ? { ...this.value } : { from: null, to: null }
                    const parts = event.target.value.match(/^(.+)\s*--\s*(.+)$/)
                    if(!parts) parsed_value.from = this.parse_date(event.target.value)
                    else {
                        ["from", "to"].forEach((attribute, index) => {
                            const part_value = parts[index + 1].trim()
                            if(part_value) parsed_value[attribute] = this.parse_date(part_value)
                        })
                    }
                    this.date_changed(parsed_value)
                } else {
                    parsed_value = this.parse_date(event.target.value)
                    this.set_date(parsed_value)
                }
            }

            this.picking = false
            /*
            const is_calendar_clicked = !!event.relatedTarget && !!this.$refs.calendar && this.$refs.calendar.contains(event.relatedTarget)
            if(!is_calendar_clicked) {
                let date
                try {
                    date = this.$moment(chrono.parseDate(event.target.value))
                } catch (error) {
                    date = null
                }
                if(this.$moment(this.value).format(this.dateFormat) !== date.format(this.dateFormat)) this.$emit("input", date, this.name)
                this.picking = false
            }
            */
        },
        click_icon () {
            this.focus_field()
        },
        pick (date) {
            this.set_date(date)
            if(this.closeOnClick) this.picking = false
        }
    },
    computed: {
        display_value () {
            if(!this.value) return ""
            return this.$moment(this.value).format(this.dateFormat)
        }
    },
    props: {
        id: {
            type: String,
            validator: prop => !!prop
        },
        name: {
            type: String,
            validator: prop => !!prop,
            required: true
        },
        placeholder: {
            type: String,
            default: ""
        },
        value: { // null, [dates or ranges], date or range ; date is moment or string, range is {from: <date>, to: <date>}
            default: null
        },
        dateFormat: {
            type: String,
            default: "MM/DD/YYYY"
        },
        position: { // use "over" for dialogs, where the popup spills out: "over" will place the calendar popup "over" the field/trigger
            validator: prop => {
                return !prop || (typeof prop === "string") || !!prop.match(/^(top|bottom|left|right|over)$/i)
            },
            default: "top"
        },
        min: { // null, string or moment
            default: null
        },
        max: { // null, string or moment
            default: null
        },
        ranged: {
            type: Boolean,
            default: false
        },
        size: {
            type: String,
            validator: prop => !!prop && prop.match(/^(sm|md|lg)$/i),
            default: "md"
        },
        closeOnClick: {
            type: Boolean,
            default: true
        },
        editable: {
            type: Boolean,
            default: true
        },
        required: {
            type: Boolean,
            required: true
        },
        disabled: {
            type: Boolean,
            default: false
        },
        saving: {
            type: Boolean,
            default: false
        },
        error: {
            type: String,
            default: ""
        }
    },
    data () {
        return {
            range_field: "from",
            picking: false
        }
    }
}
</script>

<style lang="scss">
.base-date-input {
    width: 100%;
}
</style>
